import {Form} from 'antd';
import {useState, useCallback, useEffect} from 'react';
import {useMutation} from '@apollo/client';
import {toast} from 'react-toastify';
import {TRANSLATE_MULTIPLE_FIELDS} from 'graphql/queries/investorUpdates';
import {onTranslateAnnouncement} from '../actions';
import {
  UploadVideoToBucket,
  resizeImageFile,
  UploadDocumentToBucket,
} from 'pages/campaigns/new/screens/legalDocument/uploadDocument';

export const usePopupAnnouncement = () => {
  const [form] = Form.useForm();
  const [imgUrl, setImgUrl] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');

  const [translateText, {loading: loadingTranslation}] = useMutation(
    TRANSLATE_MULTIPLE_FIELDS,
  );

  useEffect(() => {}, []);

  const handleTranslation = useCallback(
    async (
      announcementIndex,
      language,
      selectedTranslationLanguage,
      title,
      subTitle,
      actionButtonText,
    ) => {
      const translateProps = {
        sourceLang: language,
        targetLang: selectedTranslationLanguage,
        targetText1: title,
        targetText2: subTitle,
        targetText3: actionButtonText,
      };

      try {
        const currentAnnouncements = form.getFieldValue('announcements') || [];

        if (currentAnnouncements[announcementIndex]) {
          const triggerTranslation = await onTranslateAnnouncement(
            translateText,
            translateProps,
          );

          if (!triggerTranslation) {
            return toast.error('Something went wrong when Translating');
          }

          // Update the specific announcement with translation data
          currentAnnouncements[announcementIndex] = {
            ...currentAnnouncements[announcementIndex],
            multilang: {
              title: triggerTranslation.title,
              subTitle: triggerTranslation.subTitle,
              actionButtonText: triggerTranslation.actionButtonText,
              language: selectedTranslationLanguage,
            },
          };

          form.setFieldsValue({
            announcements: currentAnnouncements,
          });

          toast.success('Translation completed successfully!');
        }
      } catch (error) {
        console.error('Translation error:', error);
        toast.error('Something went wrong during translation!');
      }
    },
    [form, translateText],
  );

  const handleUploadAudio = useCallback(
    async (e, announcementIndex, multilang = false) => {
      const selectedFile = e.target.files[0];
      const currentAnnouncements = form.getFieldValue('announcements') || [];
      currentAnnouncements[announcementIndex] = {
        ...currentAnnouncements[announcementIndex],
        ...(multilang
          ? {
              multilang: {
                ...currentAnnouncements[announcementIndex].multilang,
                loadingAudioUpload: true,
              },
            }
          : {loadingAudioUpload: true}),
      };
      form.setFieldsValue({announcements: currentAnnouncements});
      const response = await UploadVideoToBucket(
        selectedFile,
        'annoucement-images',
        progress => {
          currentAnnouncements[announcementIndex] = {
            ...currentAnnouncements[announcementIndex],
            ...(multilang
              ? {
                  multilang: {
                    ...currentAnnouncements[announcementIndex].multilang,
                    audioProgress: progress,
                  },
                }
              : {audioProgress: progress}),
          };
        },
      );
      currentAnnouncements[announcementIndex] = {
        ...currentAnnouncements[announcementIndex],
        ...(multilang
          ? {
              multilang: {
                ...currentAnnouncements[announcementIndex].multilang,
                audioFile: response,
                loadingAudioUpload: false,
                audioProgress: 0,
              },
            }
          : {audioFile: response, loadingAudioUpload: false, audioProgress: 0}),
      };
      form.setFieldsValue({announcements: currentAnnouncements});
    },
    [form],
  );

  const handleUploadVideo = useCallback(
    async (e, announcementIndex, multilang = false) => {
      const selectedFile = e.target.files[0];
      if (selectedFile.type === 'video/mp4') {
        const currentAnnouncements = form.getFieldValue('announcements') || [];

        if (currentAnnouncements[announcementIndex]) {
          currentAnnouncements[announcementIndex] = {
            ...currentAnnouncements[announcementIndex],
            ...(multilang
              ? {
                  multilang: {
                    ...currentAnnouncements[announcementIndex].multilang,
                    loadingVideoUpload: true,
                  },
                }
              : {loadingVideoUpload: true}),
          };
          form.setFieldsValue({announcements: currentAnnouncements});
          try {
            const response = await UploadVideoToBucket(
              selectedFile,
              'annoucement-images',
              progress => {
                const updatedAnnouncements =
                  form.getFieldValue('announcements') || [];
                updatedAnnouncements[announcementIndex] = {
                  ...updatedAnnouncements[announcementIndex],
                  ...(multilang
                    ? {
                        multilang: {
                          ...updatedAnnouncements[announcementIndex].multilang,
                          videoProgress: progress,
                        },
                      }
                    : {videoProgress: progress}),
                };
                form.setFieldsValue({announcements: updatedAnnouncements});
              },
            );

            currentAnnouncements[announcementIndex] = {
              ...currentAnnouncements[announcementIndex],
              ...(multilang
                ? {
                    multilang: {
                      ...currentAnnouncements[announcementIndex].multilang,
                      videoUrl: response,
                      loadingVideoUpload: false,
                      videoProgress: 0,
                    },
                  }
                : {
                    videoUrl: response,
                    loadingVideoUpload: false,
                    videoProgress: 0,
                  }),
            };
            form.setFieldsValue({announcements: currentAnnouncements});

            toast.success('Video uploaded successfully!');
          } catch (error) {
            currentAnnouncements[announcementIndex] = {
              ...currentAnnouncements[announcementIndex],
              ...(multilang
                ? {
                    multilang: {
                      ...currentAnnouncements[announcementIndex].multilang,
                      loadingVideoUpload: false,
                      videoProgress: 0,
                    },
                  }
                : {
                    loadingVideoUpload: false,
                    videoProgress: 0,
                  }),
            };
            form.setFieldsValue({announcements: currentAnnouncements});
            toast.error('Failed to upload video');
          }
        }
      } else {
        toast.error('Please upload a mp4 video');
      }
    },
    [form],
  );

  const handleUploadImage = useCallback(
    async (e, announcementIndex, multilang = false) => {
      const selectedFile = e?.target?.files?.[0];
      if (!selectedFile) return;
      if (!selectedFile.type?.startsWith('image/')) {
        toast.error('Please upload a valid image file');
        return;
      }

      const currentAnnouncements = form.getFieldValue('announcements') || [];
      currentAnnouncements[announcementIndex] = {
        ...currentAnnouncements[announcementIndex],
        ...(multilang
          ? {
              multilang: {
                ...currentAnnouncements[announcementIndex]?.multilang,
                loadingImageUpload: true,
              },
            }
          : {loadingImageUpload: true}),
      };
      form.setFieldsValue({announcements: currentAnnouncements});

      try {
        const resized = await resizeImageFile(selectedFile);
        const response = await UploadDocumentToBucket(
          resized,
          progress => {
            const updated = form.getFieldValue('announcements') || [];
            updated[announcementIndex] = {
              ...updated[announcementIndex],
              ...(multilang
                ? {
                    multilang: {
                      ...updated[announcementIndex]?.multilang,
                      imageProgress: progress,
                    },
                  }
                : {imageProgress: progress}),
            };
            form.setFieldsValue({announcements: updated});
          },
          'annoucement-images',
        );

        const updated = form.getFieldValue('announcements') || [];
        updated[announcementIndex] = {
          ...updated[announcementIndex],
          ...(multilang
            ? {
                multilang: {
                  ...updated[announcementIndex]?.multilang,
                  imageUrl: response,
                  loadingImageUpload: false,
                  imageProgress: 0,
                },
              }
            : {
                imageUrl: response,
                loadingImageUpload: false,
                imageProgress: 0,
              }),
        };
        form.setFieldsValue({announcements: updated});
      } catch (error) {
        const updated = form.getFieldValue('announcements') || [];
        updated[announcementIndex] = {
          ...updated[announcementIndex],
          ...(multilang
            ? {
                multilang: {
                  ...updated[announcementIndex]?.multilang,
                  loadingImageUpload: false,
                  imageProgress: 0,
                },
              }
            : {loadingImageUpload: false, imageProgress: 0}),
        };
        form.setFieldsValue({announcements: updated});
        toast.error('Failed to upload image');
      }
    },
    [form],
  );

  const clearImageFile = useCallback(
    (announcementIndex, multilang = false) => {
      const currentAnnouncements = form.getFieldValue('announcements') || [];
      currentAnnouncements[announcementIndex] = {
        ...currentAnnouncements[announcementIndex],
        ...(multilang
          ? {
              multilang: {
                ...currentAnnouncements[announcementIndex]?.multilang,
                imageUrl: '',
                imageProgress: 0,
                loadingImageUpload: false,
              },
            }
          : {imageUrl: '', imageProgress: 0, loadingImageUpload: false}),
      };
      form.setFieldsValue({announcements: currentAnnouncements});
    },
    [form],
  );

  const clearAudioFile = useCallback(
    (announcementIndex, multilang = false) => {
      const currentAnnouncements = form.getFieldValue('announcements') || [];
      currentAnnouncements[announcementIndex] = {
        ...currentAnnouncements[announcementIndex],
        ...(multilang
          ? {
              multilang: {
                ...currentAnnouncements[announcementIndex].multilang,
                audioFile: '',
                audioProgress: 0,
              },
            }
          : {audioFile: '', audioProgress: 0}),
      };
      form.setFieldsValue({announcements: currentAnnouncements});
    },
    [form],
  );

  const clearVideoFile = useCallback(
    (announcementIndex, multilang = false) => {
      const currentAnnouncements = form.getFieldValue('announcements') || [];

      if (currentAnnouncements[announcementIndex]) {
        currentAnnouncements[announcementIndex] = {
          ...currentAnnouncements[announcementIndex],
          ...(multilang
            ? {
                multilang: {
                  ...currentAnnouncements[announcementIndex].multilang,
                  videoUrl: '',
                  videoProgress: 0,
                },
              }
            : {
                videoUrl: '',
                videoProgress: 0,
              }),
        };
        form.setFieldsValue({announcements: currentAnnouncements});
      }
    },
    [form],
  );

  const handleChangeTranslatedValues = useCallback(
    (announcementIndex, value, fieldName) => {
      const currentAnnouncements = form.getFieldValue('announcements') || [];

      if (currentAnnouncements[announcementIndex]) {
        currentAnnouncements[announcementIndex] = {
          ...currentAnnouncements[announcementIndex],
          multilang: {
            ...currentAnnouncements[announcementIndex].multilang,
            [fieldName]: value,
          },
        };
        form.setFieldsValue({announcements: currentAnnouncements});
      }
    },
    [form],
  );

  return {
    form,
    imgUrl,
    setImgUrl,
    previewUrl,
    setPreviewUrl,
    loadingTranslation,
    handleTranslation,
    handleUploadAudio,
    handleChangeTranslatedValues,
    handleUploadVideo,
    handleUploadImage,
    clearAudioFile,
    clearImageFile,
    clearVideoFile,
  };
};
